import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const LiveChat = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);

  const translations = {
    en: {
      title: 'Live Chat',
      subtitle: 'Get instant help from our team',
      namePlaceholder: 'Your Name',
      emailPlaceholder: 'Your Email',
      messagePlaceholder: 'How can we help you?',
      startChat: 'Start Chat',
      sendMessage: 'Send',
      typingPlaceholder: 'Type your message...',
      online: 'We\'re online',
      offline: 'We typically reply within 24 hours',
      welcomeMessage: 'Hello! I\'m here to help with your roofing project. What type of roofing service are you interested in?',
      quickReplies: ['Quote Request', 'Flat Roofing', 'Roof Repairs', 'Emergency Service'],
      autoReplies: {
        'quote': 'Great! For a detailed quote, please fill out our quote form with your project details. You can find it in the "Request a Quote" section. Or call us directly at +31 6 2337 0072.',
        'flat': 'We specialize in commercial flat roofing! We\'ve completed projects for Google, Heineken, and Van der Valk. Would you like to discuss your specific project requirements?',
        'repair': 'We offer 24/7 emergency repair services across the Netherlands. For urgent matters, please call +31 6 2337 0072 immediately. For scheduled repairs, tell me more about the issue.',
        'emergency': 'For emergency roofing services, please call us immediately at +31 6 2337 0072. We provide 24/7 emergency response across the Netherlands.',
        'default': 'Thank you for your message! Our team will review your inquiry and respond shortly. For urgent matters, please call +31 6 2337 0072.',
      },
    },
    nl: {
      title: 'Live Chat',
      subtitle: 'Ontvang direct hulp van ons team',
      namePlaceholder: 'Uw Naam',
      emailPlaceholder: 'Uw E-mail',
      messagePlaceholder: 'Hoe kunnen we u helpen?',
      startChat: 'Start Chat',
      sendMessage: 'Verstuur',
      typingPlaceholder: 'Typ uw bericht...',
      online: 'We zijn online',
      offline: 'We reageren meestal binnen 24 uur',
      welcomeMessage: 'Hallo! Ik ben hier om u te helpen met uw dakproject. Welk type dakwerkdienst zoekt u?',
      quickReplies: ['Offerte Aanvragen', 'Platte Daken', 'Dakreparaties', 'Noodservice'],
      autoReplies: {
        'quote': 'Geweldig! Voor een gedetailleerde offerte vult u ons offerteformulier in met uw projectdetails. U vindt dit in de sectie "Offerte Aanvragen". Of bel ons direct op +31 6 2337 0072.',
        'flat': 'Wij zijn gespecialiseerd in commerciële platte daken! We hebben projecten voltooid voor Google, Heineken en Van der Valk. Wilt u uw specifieke projecteisen bespreken?',
        'repair': 'We bieden 24/7 noodreparatiediensten door heel Nederland. Voor urgente zaken belt u direct +31 6 2337 0072. Voor geplande reparaties, vertel me meer over het probleem.',
        'emergency': 'Voor noodservice dakwerk, bel ons direct op +31 6 2337 0072. Wij bieden 24/7 noodrespons door heel Nederland.',
        'default': 'Bedankt voor uw bericht! Ons team zal uw vraag bekijken en snel reageren. Voor dringende zaken, bel +31 6 2337 0072.',
      },
    },
  };

  const ct = translations[language];

  const handleStartChat = (e) => {
    e.preventDefault();
    if (name && email) {
      setChatStarted(true);
      setMessages([
        {
          type: 'bot',
          text: ct.welcomeMessage,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          quickReplies: ct.quickReplies,
        },
      ]);
      toast.success(language === 'en' ? 'Chat started!' : 'Chat gestart!');
    }
  };

  const handleQuickReply = (reply) => {
    const userMessage = {
      type: 'user',
      text: reply,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    };
    setMessages([...messages, userMessage]);

    // Generate appropriate response
    setTimeout(() => {
      let responseText = ct.autoReplies.default;
      const lowerReply = reply.toLowerCase();
      
      if (lowerReply.includes('quote') || lowerReply.includes('offerte')) {
        responseText = ct.autoReplies.quote;
      } else if (lowerReply.includes('flat') || lowerReply.includes('plat')) {
        responseText = ct.autoReplies.flat;
      } else if (lowerReply.includes('repair') || lowerReply.includes('reparatie')) {
        responseText = ct.autoReplies.repair;
      } else if (lowerReply.includes('emergency') || lowerReply.includes('nood')) {
        responseText = ct.autoReplies.emergency;
      }

      const botReply = {
        type: 'bot',
        text: responseText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages((prev) => [...prev, botReply]);
    }, 1000);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (message.trim()) {
      const newMessage = {
        type: 'user',
        text: message,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
      setMessages([...messages, newMessage]);
      setMessage('');

      // Intelligent auto-reply based on message content
      setTimeout(() => {
        let responseText = ct.autoReplies.default;
        const lowerMessage = message.toLowerCase();
        
        if (lowerMessage.includes('quote') || lowerMessage.includes('offerte') || lowerMessage.includes('prijs') || lowerMessage.includes('price')) {
          responseText = ct.autoReplies.quote;
        } else if (lowerMessage.includes('flat') || lowerMessage.includes('plat')) {
          responseText = ct.autoReplies.flat;
        } else if (lowerMessage.includes('repair') || lowerMessage.includes('reparatie') || lowerMessage.includes('fix') || lowerMessage.includes('lek')) {
          responseText = ct.autoReplies.repair;
        } else if (lowerMessage.includes('emergency') || lowerMessage.includes('urgent') || lowerMessage.includes('nood') || lowerMessage.includes('spoed')) {
          responseText = ct.autoReplies.emergency;
        }

        const autoReply = {
          type: 'bot',
          text: responseText,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        setMessages((prev) => [...prev, autoReply]);
      }, 1500);
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 bg-[#60a5fa] text-[#0f172a] w-16 h-16 rounded-full shadow-2xl flex items-center justify-center hover:bg-[#3b82f6] transition-all duration-300 hover:scale-110"
          aria-label="Open live chat"
        >
          <MessageCircle className="w-7 h-7" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-[#1e293b] border border-[#1e40af] rounded-lg shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1e40af] to-[#3b82f6] p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-[#10b981] rounded-full animate-pulse"></div>
              <div>
                <h3 className="text-white font-semibold">{ct.title}</h3>
                <p className="text-[#93c5fd] text-sm">{ct.online}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:text-[#93c5fd] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Body */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: '400px' }}>
            {!chatStarted ? (
              // Start Chat Form
              <form onSubmit={handleStartChat} className="p-4 space-y-4">
                <p className="text-[#94a3b8] text-sm">{ct.subtitle}</p>
                <Input
                  type="text"
                  placeholder={ct.namePlaceholder}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa]"
                />
                <Input
                  type="email"
                  placeholder={ct.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa]"
                />
                <Textarea
                  placeholder={ct.messagePlaceholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="bg-[#0f172a] border-[#1e40af] text-[#60a5fa]"
                />
                <Button
                  type="submit"
                  className="w-full bg-[#60a5fa] hover:bg-[#3b82f6] text-[#0f172a]"
                >
                  {ct.startChat}
                </Button>
              </form>
            ) : (
              // Chat Messages
              <div className="p-4 space-y-3">
                {messages.map((msg, index) => (
                  <div key={index}>
                    <div
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.type === 'user'
                            ? 'bg-[#60a5fa] text-[#0f172a]'
                            : 'bg-[#0f172a] text-[#94a3b8] border border-[#1e40af]'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <p className="text-xs mt-1 opacity-70">{msg.time}</p>
                      </div>
                    </div>
                    {/* Quick Replies */}
                    {msg.quickReplies && (
                      <div className="flex flex-wrap gap-2 mt-2 ml-2">
                        {msg.quickReplies.map((reply, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleQuickReply(reply)}
                            className="text-xs bg-[#0f172a] text-[#60a5fa] border border-[#1e40af] px-3 py-1 rounded-full hover:bg-[#1e40af] transition-colors"
                          >
                            {reply}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message Input (only shown after chat started) */}
          {chatStarted && (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-[#1e40af] bg-[#0f172a]">
              <div className="flex space-x-2">
                <Input
                  type="text"
                  placeholder={ct.typingPlaceholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="flex-1 bg-[#1e293b] border-[#1e40af] text-[#60a5fa]"
                />
                <Button
                  type="submit"
                  className="bg-[#60a5fa] hover:bg-[#3b82f6] text-[#0f172a] px-4"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  );
};

export default LiveChat;
